import { Autocomplete, Box, Typography } from "@mui/material";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import {
    capitalize,
    debounce,
    isEmpty,
    isNil,
    isString,
    isUndefined
} from "lodash";
import { useCallback, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import HighlightBorderTextField, {
    colorStatus
} from "./highlightBorderTextField";
import impressService from "services/impress";
import { diagnosisGroup } from "dto/encounter.dto";
// import useUmami from "hooks/useUmami";

export type Value = {
    keyword: string;
    conceptId?: string;
    sctId?: string;
    recommend?: boolean;
    axisName?: string;
    icd: { code: string; term: string }[];
    stack: any;
};

export type PostCoordinationDiagnosisAutoCompleteProps = {
    setState?: (val: Value) => void;
    isPrefill?: boolean;
    colorStatus?: colorStatus;
    defaultValue?: string;
    externalCause?: boolean;
    icdVersion?: "ICD10-WHO-2016" | "ICD10-WHO-2010";
    stack?: any;
    autoFocus?: boolean;
    disabledFreeText?: boolean;
    resetOnSelect?: boolean;
    recentData?: diagnosisGroup;
};

const PostCoordinationDiagnosisAutoComplete = (
    props: PostCoordinationDiagnosisAutoCompleteProps
) => {
    const {
        setState,
        isPrefill,
        defaultValue = "",
        colorStatus = "default",
        externalCause = false,
        icdVersion = "ICD10-WHO-2016",
        stack,
        autoFocus = false,
        disabledFreeText = false,
        resetOnSelect = false,
        recentData
    } = props;
    const [value, setValue] = useState<Value>({
        keyword: defaultValue,
        icd: [],
        stack: stack ?? ""
    });

    const [options, setOptions] = useState<Value[]>([]);

    // function capitalizeWords(input: string): string {
    //     return input.replace(/\b\w/g, (char) => char.toUpperCase());
    // }

    // const { track } = useUmami();

    const searchPostCo = useCallback(
        async (keyword: string, stack?: any) => {
            const payload = {
                keyword,
                stack: stack ?? value.stack,
                forceTermReplace: true,
                filterConditions: {
                    externalCause: externalCause,
                    icdVersion: externalCause ? "ICD10-WHO-2010" : icdVersion
                }
            };
            const { data } = await impressService.postCoordinationV2(payload);

            // track("search-post-coordination", {
            //     ...payload,
            //     stack: undefined
            // });

            const options = data.hits.map((i) => {
                return {
                    keyword: capitalize(i.wholeTermReplace),
                    icd: i.icd.map((i) => {
                        const [code, term] = i.split(":");
                        return {
                            code,
                            term
                        };
                    }),
                    stack: i.stack,
                    conceptId: i.conceptId
                };
            });
            const filterDuplicated = options.filter((i) => {
                const symptomName = i.keyword;
                const allSymptoms = [
                    recentData?.principal[0].term,
                    ...(recentData?.comorbidity?.map((c) => c.term) || []),
                    ...(recentData?.complication?.map((c) => c.term) || []),
                    ...(recentData?.other?.map((c) => c.term) || []),
                    ...(recentData?.externalCause?.map((c) => c.term) || [])
                ];

                return !allSymptoms.includes(symptomName);
            });

            if (disabledFreeText) {
                setOptions(options);
            } else {
                const _extendOptions = [
                    ...filterDuplicated,
                    {
                        keyword: capitalize(keyword),
                        icd: [],
                        stack: "",
                        conceptId: ""
                    }
                ];

                setOptions(_extendOptions);
            }
        },
        [
            disabledFreeText,
            externalCause,
            icdVersion,
            recentData?.comorbidity,
            recentData?.complication,
            recentData?.externalCause,
            recentData?.other,
            recentData?.principal,
            value.stack
        ]
    );

    const handleDebounceSearch = useMemo(() => {
        return debounce(searchPostCo, 300);
    }, [searchPostCo]);

    const inputValidation = (inputValue: string) => {
        const validCharacters = /^[a-zA-Z0-9ก-๙+/*\-.,{}[\]():' ]*$/;
        const isIncludeThaiNumber = inputValue.match(/[๑-๙]/g);
        return validCharacters.test(inputValue) && !isIncludeThaiNumber;
    };

    const handleInputChange = (inputValue: string, reason: string) => {
        if (reason === "input") {
            if (isEmpty(inputValue)) {
                setValue((prev) => ({ ...prev, keyword: "" }));
            } else {
                if (inputValidation(inputValue)) {
                    setValue((prev) => ({ ...prev, keyword: inputValue }));
                    handleDebounceSearch(inputValue);
                }
            }
        }
    };

    return (
        <Autocomplete
            size="small"
            options={options}
            getOptionLabel={(option) => {
                if (isString(option)) {
                    return option;
                } else {
                    return option?.keyword ?? "-";
                }
            }}
            renderOption={(props, option, { inputValue }) => {
                const matches = match(option.keyword, inputValue, {
                    insideWords: true
                });

                const parts = parse(option.keyword, matches);
                return (
                    <li {...props} key={uuidv4()}>
                        <Box
                            width="100%"
                            sx={{
                                backgroundColor: option.recommend
                                    ? (theme) => theme.palette.lightGreen.main
                                    : undefined
                            }}
                        >
                            <Box textAlign="right" width="100%">
                                <Typography
                                    component="span"
                                    sx={{
                                        color: (theme) =>
                                            theme.palette.info.main,
                                        textTransform: "capitalize"
                                    }}
                                    variant="subtitle2"
                                >
                                    {`${option.keyword ?? ""} ${
                                        option.axisName ?? ""
                                    }`}{" "}
                                </Typography>
                                {option.icd.length > 0 ? (
                                    <Typography
                                        component="span"
                                        sx={{
                                            color: (theme) =>
                                                theme.palette.primary.main
                                        }}
                                    >
                                        {`(${option.icd
                                            .map((i) => i.code)
                                            .join(", ")})`}
                                    </Typography>
                                ) : (
                                    <Typography
                                        component="span"
                                        sx={{
                                            color: (theme) =>
                                                theme.palette.primary.main
                                        }}
                                    >
                                        {`(Free Text)`}
                                    </Typography>
                                )}
                            </Box>
                            <Box
                                sx={{
                                    textTransform: "capitalize"
                                }}
                            >
                                {parts.map((part, index) => (
                                    <span
                                        key={index}
                                        style={{
                                            fontWeight: part.highlight
                                                ? 800
                                                : undefined
                                        }}
                                    >
                                        {part.text}
                                    </span>
                                ))}
                            </Box>
                        </Box>
                    </li>
                );
            }}
            freeSolo
            onInputChange={(_, value, reason) =>
                handleInputChange(value, reason)
            }
            onChange={(_, value) => {
                if (isString(value)) {
                    setValue({ keyword: value, icd: [], stack: "" });
                    handleDebounceSearch(value);
                } else if (isNil(value)) {
                    setValue({ keyword: "", icd: [], stack: "" });
                } else {
                    setValue(value);
                    if (!isUndefined(setState)) setState(value);
                    handleDebounceSearch(value.keyword, value.stack);
                }

                if (resetOnSelect) {
                    setValue({ keyword: "", icd: [], stack: "" });
                }
            }}
            filterOptions={(options) => {
                return options;
            }}
            value={value}
            renderInput={(params) => {
                return (
                    <HighlightBorderTextField
                        {...params}
                        value={
                            inputValidation(params.inputProps.value as string)
                                ? params.inputProps.value
                                : ""
                        }
                        colorStatus={colorStatus}
                        isAuto={isPrefill}
                        disabled={isPrefill}
                        placeholder="Diagnosis (Post-coordination)"
                        autoFocus={autoFocus}
                        inputProps={{
                            ...params.inputProps,
                            onInput: (
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                const input = e.target as HTMLInputElement;
                                if (!inputValidation(input.value)) {
                                    input.value = input.value.slice(0, -1); // Remove the last invalid character
                                }
                            }
                        }}
                    />
                );
            }}
        />
    );
};

export default PostCoordinationDiagnosisAutoComplete;
